<template>
  <div class="task-detail-basics" data-testid="setup-task-detail__basics">
    <h5 class="task-detail-basics__heading" data-testid="setup-task-detail__basics-heading">
      Details
    </h5>

    <div class="task-detail-basics__grid">
      <!-- Column 1 -->
      <div class="task-detail-basics__col task-detail-basics__col--primary">
        <div
          class="task-detail-basics__field"
          data-testid="setup-task-detail__basics-item--task_type"
        >
          <p class="task-detail-basics__label" data-testid="setup-task-detail__basics-label--task_type">
            Task Type
          </p>
          <p class="task-detail-basics__value" data-testid="setup-task-detail__basics-value--task_type">
            {{ template.task_type || '—' }}
          </p>
        </div>

        <div
          v-if="!isAR"
          class="task-detail-basics__field"
          data-testid="setup-task-detail__basics-item--cycle_type"
        >
          <p class="task-detail-basics__label" data-testid="setup-task-detail__basics-label--cycle_type">
            Cycle Type
          </p>
          <p class="task-detail-basics__value" data-testid="setup-task-detail__basics-value--cycle_type">
            {{ cycleTypeDisplay }}
          </p>
        </div>

        <div
          class="task-detail-basics__field"
          data-testid="setup-task-detail__basics-item--tags"
        >
          <p class="task-detail-basics__label" data-testid="setup-task-detail__basics-label--tags">
            Frequency
          </p>
          <div
            class="task-detail-basics__chips"
            data-testid="setup-task-detail__basics-value--tags"
          >
            <span
              v-for="(tag, index) in frequencyTags"
              :key="`freq-${index}`"
              class="task-detail-basics__chip"
              :data-testid="`setup-task-detail__basics-tag--tags-${index}`"
            >
              {{ tag }}
            </span>
            <span v-if="!frequencyTags.length" class="task-detail-basics__empty">—</span>
          </div>
        </div>

        <div
          v-if="isAR"
          class="task-detail-basics__field"
          data-testid="setup-task-detail__basics-item--assertion"
        >
          <p class="task-detail-basics__label" data-testid="setup-task-detail__basics-label--assertion">
            Assertion
          </p>
          <div
            class="task-detail-basics__chips"
            data-testid="setup-task-detail__basics-value--assertion"
          >
            <span
              v-for="(tag, index) in assertionTags"
              :key="`assertion-${index}`"
              class="task-detail-basics__chip"
              :data-testid="`setup-task-detail__basics-tag--assertion-${index}`"
            >
              {{ tag }}
            </span>
            <span v-if="!assertionTags.length" class="task-detail-basics__empty">—</span>
          </div>
        </div>
      </div>

      <!-- Column 2 -->
      <div class="task-detail-basics__col task-detail-basics__col--secondary">
        <div
          v-if="isAR"
          class="task-detail-basics__field"
          data-testid="setup-task-detail__basics-item--category"
        >
          <p class="task-detail-basics__label" data-testid="setup-task-detail__basics-label--category">
            Category
          </p>
          <p class="task-detail-basics__value" data-testid="setup-task-detail__basics-value--category">
            {{ template.category || '—' }}
          </p>
        </div>

        <div
          class="task-detail-basics__field"
          data-testid="setup-task-detail__basics-item--priority"
        >
          <p class="task-detail-basics__label" data-testid="setup-task-detail__basics-label--priority">
            Priority
          </p>
          <TaskPriorityDisplay
            v-if="template.priority"
            variant="cell"
            :priority="template.priority"
            data-test-id="setup-task-detail__basics-value--priority"
          />
          <span
            v-else
            class="task-detail-basics__empty"
            data-testid="setup-task-detail__basics-value--priority"
          >—</span>
        </div>

        <div
          class="task-detail-basics__field"
          data-testid="setup-task-detail__basics-item--duedays"
        >
          <p class="task-detail-basics__label" data-testid="setup-task-detail__basics-label--duedays">
            Due Days
          </p>
          <p class="task-detail-basics__value" data-testid="setup-task-detail__basics-value--duedays">
            {{ dueDaysDisplay }}
          </p>
        </div>

        <div
          class="task-detail-basics__field"
          data-testid="setup-task-detail__basics-item--no_of_evidence"
        >
          <p class="task-detail-basics__label" data-testid="setup-task-detail__basics-label--no_of_evidence">
            No of Evidence
          </p>
          <p class="task-detail-basics__value" data-testid="setup-task-detail__basics-value--no_of_evidence">
            {{ evidenceDisplay }}
          </p>
        </div>
      </div>

      <!-- Column 3 -->
      <div class="task-detail-basics__col task-detail-basics__col--tertiary">
        <div
          v-if="isAR"
          class="task-detail-basics__field"
          data-testid="setup-task-detail__basics-item--subcategory"
        >
          <p class="task-detail-basics__label" data-testid="setup-task-detail__basics-label--subcategory">
            Sub Category
          </p>
          <p class="task-detail-basics__value" data-testid="setup-task-detail__basics-value--subcategory">
            {{ template.subcategory || '—' }}
          </p>
        </div>

        <div
          class="task-detail-basics__field"
          data-testid="setup-task-detail__basics-item--purpose"
        >
          <p class="task-detail-basics__label" data-testid="setup-task-detail__basics-label--purpose">
            Purpose
          </p>
          <p class="task-detail-basics__value task-detail-basics__value--body" data-testid="setup-task-detail__basics-value--purpose">
            {{ purposeText || '—' }}
          </p>
        </div>

        <div
          class="task-detail-basics__field"
          data-testid="setup-task-detail__basics-item--instruction"
        >
          <p class="task-detail-basics__label" data-testid="setup-task-detail__basics-label--instruction">
            Instruction
          </p>
          <p class="task-detail-basics__value task-detail-basics__value--body" data-testid="setup-task-detail__basics-value--instruction">
            {{ instructionText || '—' }}
          </p>
        </div>
      </div>
    </div>

    <div
      v-if="customFieldItems.length"
      class="task-detail-basics__custom"
      data-testid="setup-task-detail__basics-custom-fields"
    >
      <div
        v-for="field in customFieldItems"
        :key="field.columnName"
        class="task-detail-basics__field"
        :data-testid="`setup-task-detail__basics-item--${field.columnName}`"
      >
        <p
          class="task-detail-basics__label"
          :data-testid="`setup-task-detail__basics-label--${field.columnName}`"
        >
          {{ field.label }}
        </p>
        <p
          class="task-detail-basics__value"
          :data-testid="`setup-task-detail__basics-value--${field.columnName}`"
        >
          {{ field.value }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import './TaskDetailBasics.scss';
import { isAccountReconciliation, getCustomFieldValue } from '../../../TaskList/utils/taskListHelpers';
import TaskPriorityDisplay from '../../../../Project/TaskList/components/TaskPriorityDisplay/TaskPriorityDisplay.vue';

export default {
  name: 'TaskDetailBasics',

  components: {
    TaskPriorityDisplay,
  },

  props: {
    template: {
      type: Object,
      required: true,
    },
    customFields: {
      type: Array,
      default: () => [],
    },
  },

  computed: {
    isAR() {
      return isAccountReconciliation(this.template.task_type, this);
    },

    cycleTypeDisplay() {
      return (this.template.cycle_type || this.template.cycleType || '—')
        .toString()
        .toUpperCase();
    },

    purposeText() {
      return (this.template.purpose || '').trim();
    },

    instructionText() {
      return (this.template.instruction || '').trim();
    },

    frequencyTags() {
      return this.normalizeList(this.template.tags || this.template.frequency);
    },

    assertionTags() {
      return this.normalizeList(this.template.assertion || this.template.assertions);
    },

    dueDaysDisplay() {
      const value = this.template.duedays ?? this.template.due_days;
      return value != null && value !== '' ? String(value) : '—';
    },

    evidenceDisplay() {
      const value =
        this.template.no_of_evidence ??
        this.template.noofevidence ??
        this.template.noOfEvidence;
      return value != null && value !== '' ? String(value) : '—';
    },

    customFieldItems() {
      return (this.customFields || [])
        .filter((field) => field?.columnName && !field.isSystem)
        .map((field) => {
          const raw = getCustomFieldValue(this.template, field.columnName);
          return {
            columnName: field.columnName,
            label: field.displayName || field.displayText || field.columnName,
            value: raw === '' || raw == null ? '—' : String(raw),
          };
        });
    },
  },

  methods: {
    normalizeList(value) {
      if (Array.isArray(value)) {
        return value
          .filter(Boolean)
          .map((item) =>
            typeof item === 'object'
              ? item.name || item.label || String(item)
              : String(item)
          );
      }
      if (value == null || value === '') return [];
      return [String(value)];
    },
  },
};
</script>
